import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, SelectGeneric, Toggle } from 'ypf-pas-general-ui-components';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';

import { initialFilters } from '../../hooks/useFilters';
import {
  fetchDataTeam,
  fetchServices
} from '../../../../page/ServiceRequest/FilterServiceRequest/ContentFilter/helpers/filtersFetcher';
import { ubicationTypeEnum } from '../../../../utils/dataStatus';
import { ACTIVE_KEY, LOCATION_TYPE_KEY, OPTION_PARTES } from '../../utils/consts';
import {
  fetchAprooverContracts,
  fetchInspectorContracts,
  fetchLocations,
  fetchObjectImputation,
  fetchProveedores,
  fetchProvidersForAproover,
  fetchRequestService,
  getWellEventDateAdapterFunction
} from '../../utils/customFetchFunctions';
import useToast from '../../../../hooks/useToast';
import { tryCatchMaker } from '../../../../utils/globalFunctions';
import { filtersAdapterToSave } from '../../../../adapters/weekly-report/filters-adapter';
import { saveFilters } from '../../../../services/userFilterServices';
import useFilterParams from '../../hooks/useFilterParams';
import DateInputCalendar from '../../../Calendar/DateInputCalendar/DateInputCalendar';
import { getRequestsServiceByApprover } from '../../../../services/serviceRequest';

import styles from './WeeklyReportFilters.module.scss';
import PositionLineFilter from './PositionLineFilter/PositionLineFilter';

const checkIsOyM = (tipoUbicacionEnum) =>
  tipoUbicacionEnum?.id !== ubicationTypeEnum.locacion &&
  tipoUbicacionEnum?.id !== ubicationTypeEnum.pozo;

const getSetValue = (isOyM, values) =>
  isOyM ? values?.operacionesIds : values?.unidadesNegocioIds;

const getSetFieldName = (isOyM) => (isOyM ? 'operacionesIds' : 'unidadesNegocioIds');

/**
 * @typedef Props
 * @property {import("../../../../adapters/weekly-report/weekly-report-calendar-adapter").TableDataCalendar} calendar
 * @property {() => void} cleanFilters
 * @property {boolean} isInspector
 * @property {import('../../hooks/useFilters').Filters} filters
 * @property {(newFilters: import('../../hooks/useFilters').Filters) => void} updateFilters
 */

/** @param {Props} */
const WeeklyReportFilters = ({
  calendar,
  cleanFilters,
  filters,
  isInspector,
  isApprover,
  updateFilters,
  setShowFilters
}) => {
  const [formData, setFormData] = useState(filters);
  const {
    ubicationTypeSelect,
    events,
    workPartStatus,
    userBusinessUnits,
    userBusinessUnitsIds,
    logisticCenters,
    imputationTypes = []
  } = useFilterParams(
    isInspector,
    // This true it's for include generic ubication
    true,
    formData.tipoUbicacionId
  );

  const { setNewToast } = useToast();
  const [workpartFilter, setWorkpartFilter] = useState(filters.partesVisualizar);
  const [wellOptions, setWellOptions] = useState([]);

  const refPositionLineFilter = useRef();

  useEffect(() => {
    setFormData(filters);
  }, [filters]);

  const ubicationTypeOptions = useMemo(
    () => ubicationTypeSelect.filter((e) => e.id !== ubicationTypeEnum.locacion) || [],
    [ubicationTypeSelect]
  );

  /**
   * @param {import('react').FormEvent<HTMLFormElement>} event
   * @returns {({import('../../hooks/useFilters').Filters}) => void}
   */
  const handleOnSubmit = (event) => {
    event.preventDefault();
    updateFilters({
      ...formData,
      posicionesLineas: refPositionLineFilter?.current?.positionLine
    });
    setShowFilters((oldValue) => !oldValue);
  };

  /**
   * Valida si los parámetros para buscar pozos-eventos-fecha están completos.
   * @param {{ idsEventos: any[], fechaDesde: string, fechaHasta: string }} params - Parámetros del formulario.
   * @returns {boolean} True si todos los campos tienen valor, false en caso contrario.
   */
  function checkValid({ idsEventos, fechaDesde, fechaHasta }) {
    return fechaDesde?.length !== 0 && fechaHasta?.length !== 0 && idsEventos?.length !== 0;
  }

  /**
   * Obtiene y setea las opciones de pozo-evento-fecha para el filtro.
   * @param {{ idsEventos: any[], fechaDesde: string, fechaHasta: string }} params - Parámetros del formulario.
   * @returns {Promise<void>} Promesa que resuelve cuando se setean las opciones.
   */
  const getWellEventDateOptions = async ({ idsEventos, fechaDesde, fechaHasta }) => {
    let options = [];

    if (checkValid({ idsEventos, fechaDesde, fechaHasta })) {
      options = await getWellEventDateAdapterFunction({ idsEventos, fechaDesde, fechaHasta });
    }

    return setWellOptions(options);
  };

  useEffect(() => {
    if (formData.idsEventos?.length) {
      getWellEventDateOptions(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.idsEventos]);

  /**
   * Opciones de estado para el filtro "Estado de solicitud".
   * @type {{id: number, nombre: string}[]}
   */
  const optionsState = [
    { id: 4, nombre: 'En ejecución' },
    { id: 5, nombre: 'Finalizada' }
  ];

  /**
   * @param {keyof import('../../hooks/useFilters').Filters} key
   * @returns {(newValue: typeof VIEW_OPTIONS[number]) => void}
   */
  const handleOnChange = (key, newValue) => {
    // *Cleaning validation for location input when changing the fields locationType or active
    if (key === LOCATION_TYPE_KEY || key === ACTIVE_KEY) {
      setFormData((state) => ({
        ...state,
        ubicacionesIds: [],
        [key]: newValue
      }));
    } else {
      setFormData((state) => ({
        ...state,
        [key]: newValue
      }));
    }
  };

  const onClean = () => {
    cleanFilters();
    setFormData(initialFilters);
    setWorkpartFilter(true);
  };

  const onSave = async () => {
    const formatedFilters = filtersAdapterToSave(
      { ...formData, posicionesLineas: refPositionLineFilter?.current?.positionLine },
      isInspector
    );

    updateFilters({ ...formData, posicionesLineas: refPositionLineFilter?.current?.positionLine });

    saveFilters(formatedFilters)
      .then(() => {
        setNewToast({
          type: 'SUCCESS',
          message: 'Filtros guardados con éxito.'
        });
      })
      .catch((err) => {
        setNewToast({
          type: 'ERROR',
          message: err.message
        });
      });
  };

  const fetchProviders = (value) => {
    let promise;

    if (isInspector) {
      promise = () => fetchProveedores(value, formData?.contratosIds);
    } else {
      promise = () => fetchProvidersForAproover(value, formData?.contratosIds);
    }

    return tryCatchMaker({
      promise,
      defaultValue: [],
      setNewToast
    });
  };

  const fetchContracts = (value) => {
    const promise = isInspector
      ? () => fetchInspectorContracts(value, formData?.idsProveedores)
      : () => fetchAprooverContracts(value, formData?.idsProveedores);

    return tryCatchMaker({
      promise,
      defaultValue: [],
      setNewToast
    });
  };

  const handleWorkpartFilterSwitch = (e) => {
    setWorkpartFilter(e);
    setFormData((state) => ({ ...state, partesVisualizar: e }));
  };

  const areDatesSetted = !(formData.fechaDesde && formData.fechaHasta);

  const getServicesFetch = (newValue) =>
    isApprover ? getRequestsServiceByApprover(newValue) : fetchRequestService(newValue);

  const isOyM = checkIsOyM(formData.tipoUbicacionId);
  const setValue = getSetValue(isOyM, formData);
  const setFieldName = getSetFieldName(isOyM);

  const handleSetFieldValues = (newValues) =>
    isOyM
      ? handleOnChange('operacionesIds', newValues || [])
      : handleOnChange('unidadesNegocioIds', newValues || []);
  const onChangeLocationType = (newValues) => {
    handleOnChange('tipoUbicacionId', newValues || null);
    handleOnChange('operacionesIds', []);
    handleOnChange('unidadesNegocioIds', []);
    handleOnChange('ubicacion', []);
    if (checkIsOyM(newValues)) {
      handleOnChange('equipo', []);
    }
  };

  return (
    <form className={`mt-4 mx-2 ${styles['filter-container']}`} onSubmit={handleOnSubmit}>
      <Row className="d-flex gap-1">
        <Col>
          <DateInputCalendar
            arrayDate={calendar?.pendingDates}
            handleChangeDate={(newDate) =>
              setFormData((prev) => ({ ...prev, fechaDesde: newDate }))
            }
            label="Desde"
            maxDate={formData.fechaHasta}
            selectDay={formData.fechaDesde}
          />
        </Col>
        <Col>
          <DateInputCalendar
            arrayDate={calendar?.pendingDates}
            handleChangeDate={(newDate) =>
              setFormData((prev) => ({ ...prev, fechaHasta: newDate }))
            }
            label="Hasta"
            minDate={formData.fechaDesde}
            selectDay={formData.fechaHasta}
          />{' '}
        </Col>
        {areDatesSetted && <span className={styles['date-filter-alert']}>Campos obligatorios</span>}
      </Row>
      <Toggle
        className="d-flex justify-content-between pb-2"
        label="Partes a Visualizar"
        name="partesVisualizar"
        onChange={handleWorkpartFilterSwitch}
        options={OPTION_PARTES}
        type="radio"
        value={workpartFilter}
      />
      <SelectGeneric
        async
        fetchData={(value) => fetchProviders(value)}
        isMulti
        label="Proveedores"
        name="idsProveedores"
        noOptionsMessage="Ingresar al menos 3 caracteres"
        onChange={(newValue) => handleOnChange('idsProveedores', newValue)}
        placeholder="Buscar proveedor"
        showIcon
        value={formData.idsProveedores}
        withSelect="100%"
      />
      <SelectGeneric
        async
        fetchData={(value) => fetchContracts(value)}
        isMulti
        label="Contratos"
        name="contratosIds"
        noOptionsMessage="Ingresar al menos 3 caracteres"
        onChange={(newValue) => {
          refPositionLineFilter?.current?.resetFiltersOnChanges(newValue);
          handleOnChange('contratosIds', newValue);
        }}
        placeholder="Buscar contrato"
        showIcon
        value={formData.contratosIds}
        withSelect="100%"
      />
      <PositionLineFilter
        contratosIds={formData?.contratosIds}
        formData={formData}
        ref={refPositionLineFilter}
      />
      <Col sm={12}>
        <SelectGeneric
          async
          clearable
          defaultValueOneOption={false}
          fetchData={(newValue) => getServicesFetch(newValue)}
          isMulti
          label="Solicitud de servicio"
          name="numerosSolicitudes"
          noOptionsMessage="Debe ingresar al menos 4 caracteres"
          onChange={(newValue) => handleOnChange('numerosSolicitudes', newValue)}
          placeholder="Seleccionar solicitud servicio"
          type="text"
          value={formData.numerosSolicitudes}
          withSelect="100%"
        />
      </Col>

      {isInspector && (
        <Col sm={12}>
          <SelectGeneric
            clearable
            defaultValueOneOption={false}
            label="Estado de solicitud"
            name="estadoSolicitud"
            onChange={(newValue) => handleOnChange('estadoSolicitud', newValue)}
            options={optionsState}
            placeholder="Seleccionar estado de solicitud"
            type="text"
            value={formData.estadoSolicitud}
            withSelect="100%"
          />
        </Col>
      )}

      <SelectGeneric
        clearable
        isDisabled={!ubicationTypeSelect}
        label="Tipo de ubicación"
        name="tipoUbicacionEnum"
        onChange={onChangeLocationType}
        options={ubicationTypeOptions}
        paddingIndicator={4}
        placeholder="Seleccione tipo de ubicación"
        value={formData?.tipoUbicacionId}
        withSelect="100%"
      />
      <SelectGeneric
        clearable
        defaultValueOneOption={false}
        isDisabled={!formData?.tipoUbicacionId}
        isMulti
        label="Unidad de Negocio"
        name={setFieldName}
        onChange={(newValues) => {
          handleSetFieldValues(newValues);
        }}
        options={userBusinessUnits}
        paddingIndicator={4}
        placeholder="Seleccione una opción"
        value={setValue}
        withSelect="100%"
      />
      <SelectGeneric
        async
        fetchData={(value) =>
          tryCatchMaker({
            promise: () => fetchLocations(value, formData?.tipoUbicacionId, userBusinessUnitsIds),
            defaultValue: [],
            setNewToast
          })
        }
        isDisabled={!formData.tipoUbicacionId}
        isMulti
        label="Ubicación"
        name="ubicacionesIds"
        noOptionsMessage="Ingresar al menos 3 caracteres"
        onChange={(newValue) => handleOnChange('ubicacionesIds', newValue)}
        placeholder="Selecciona una ubicacion"
        showIcon
        value={formData.ubicacionesIds}
        withSelect="100%"
      />
      <SelectGeneric
        async
        fetchData={(value) => fetchServices(value)}
        isMulti
        label="Servicio"
        name="serviciosIds"
        noOptionsMessage="Ingresar al menos 3 caracteres"
        onChange={(newValue) => handleOnChange('serviciosIds', newValue)}
        placeholder="Buscar servicio"
        showIcon
        value={formData.serviciosIds}
        withSelect="100%"
      />
      {isApprover && (
        <Col sm={12}>
          <SelectGeneric
            clearable
            defaultValueOneOption={false}
            isMulti
            label="Estado de solicitud"
            name="estadosSolicitud"
            onChange={(newValue) => handleOnChange('estadosSolicitud', newValue)}
            options={optionsState}
            placeholder="Seleccionar estado de solicitud"
            type="text"
            value={formData.estadosSolicitud}
            withSelect="100%"
          />
        </Col>
      )}
      <SelectGeneric
        clearable
        label="Estado del parte"
        name="estadoParteId"
        onChange={(newValue) => handleOnChange('estadoParteId', newValue)}
        options={workPartStatus}
        placeholder="Selecciona un estado"
        showIcon
        value={formData.estadoParteId}
        withSelect="100%"
      />
      <SelectGeneric
        isMulti
        label="Tipo de evento"
        name="idsEventos"
        noOptionsMessage="Ingresar al menos 3 caracteres"
        onChange={(newValue) => {
          handleOnChange('idsEventos', newValue);
          handleOnChange('eventosPozosIds', null);
        }}
        options={events}
        placeholder="Buscar tipo de evento"
        showIcon
        value={formData.idsEventos}
        withSelect="100%"
      />
      {isInspector && (
        <>
          <Col sm={12}>
            <SelectGeneric
              clearable
              defaultValueOneOption={false}
              isDisabled={!checkValid(formData)}
              isMulti
              label="Pozo-Evento-Fecha"
              name="eventosPozosIds"
              onChange={(newValue) => handleOnChange('eventosPozosIds', newValue)}
              options={wellOptions}
              placeholder="Seleccionar evento por fecha"
              type="text"
              value={formData.eventosPozosIds}
              withSelect="100%"
            />
          </Col>
          <Col sm={12}>
            <SelectGeneric
              clearable
              defaultValueOneOption={false}
              isMulti
              label="Centro logístico"
              name="centrosLogisticos"
              onChange={(newValue) => handleOnChange('centrosLogisticos', newValue)}
              options={logisticCenters}
              placeholder="Seleccionar centro logístico"
              type="text"
              value={formData.centrosLogisticos}
              withSelect="100%"
            />
          </Col>
          <Col sm={12}>
            <SelectGeneric
              clearable
              defaultValueOneOption={false}
              label="Tipo de objeto de imputación"
              name="tipoImputacion"
              onChange={(newValue) => {
                handleOnChange('objetosImputacionesIds', null);
                handleOnChange('tipoImputacion', newValue);
              }}
              options={imputationTypes}
              placeholder="Seleccionar tipo objeto de imputación"
              type="text"
              value={formData.tipoImputacion}
              withSelect="100%"
            />
          </Col>
          <Col sm={12}>
            <SelectGeneric
              async
              clearable
              defaultValueOneOption={false}
              fetchData={(newValue) => fetchObjectImputation(formData.tipoImputacion, newValue)}
              isDisabled={Boolean(!formData.tipoImputacion)}
              isMulti
              label="Objeto de imputación"
              name="objetosImputacionesIds"
              noOptionsMessage="Debe ingresar al menos 5 caracteres"
              onChange={(newValue) => handleOnChange('objetosImputacionesIds', newValue)}
              placeholder="Seleccionar objeto de imputación"
              type="text"
              value={formData.objetosImputacionesIds}
              withSelect="100%"
            />
          </Col>
        </>
      )}
      <SelectGeneric
        async
        fetchData={(value) => fetchDataTeam(value)}
        isDisabled={!formData?.tipoUbicacionId}
        isMulti
        label="Equipo de torre"
        name="idsEquipos"
        noOptionsMessage="Ingresar al menos 3 caracteres"
        onChange={(newValue) => handleOnChange('idsEquipos', newValue)}
        placeholder="Buscar equipo"
        showIcon
        value={formData.idsEquipos}
        withSelect="100%"
      />
      <div className="container-buttons">
        <Button
          label="Limpiar"
          onClick={onClean}
          type="button"
          variant="outline-primary"
          widthButton="90px"
        />
        <Button
          label="Guardar"
          onClick={onSave}
          type="button"
          variant="outline-primary"
          widthButton="90px"
        />
        <Button
          disabled={!(formData.fechaDesde && formData.fechaHasta)}
          label="Buscar"
          type="submit"
          variant="primary"
          widthButton="90px"
        />
      </div>
    </form>
  );
};

export default WeeklyReportFilters;

WeeklyReportFilters.propTypes = {
  calendar: PropTypes.shape({
    firstWeek: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    lastWeek: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),
    pendingDates: PropTypes.arrayOf(PropTypes.string)
  }),
  cleanFilters: PropTypes.func,
  isInspector: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    contratosIds: PropTypes.arrayOf(PropTypes.number),
    partesVisualizar: PropTypes.bool,
    idsEquipos: PropTypes.arrayOf(PropTypes.number),
    idsEventos: PropTypes.arrayOf(PropTypes.number),
    idsProveedores: PropTypes.arrayOf(PropTypes.number),
    posicionesLineas: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string
      })
    ),
    tipoUbicacionId: PropTypes.shape({
      id: PropTypes.number,
      value: PropTypes.number,
      label: PropTypes.string,
      nombre: PropTypes.string,
      tipoNegocio: PropTypes.shape({
        evento: PropTypes.string,
        id: PropTypes.number,
        nombre: PropTypes.string
      })
    }),
    ubicacionesIds: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        value: PropTypes.number,
        label: PropTypes.string,
        nombre: PropTypes.string
      })
    ),
    serviciosIds: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        value: PropTypes.number,
        label: PropTypes.string,
        nombre: PropTypes.string
      })
    )
  }),
  updateFilters: PropTypes.func,
  setShowFilters: PropTypes.func,
  isApprover: PropTypes.bool
};

WeeklyReportFilters.defaultProps = {
  isApprover: false,
  calendar: {},
  cleanFilters: () => {},
  filters: {},
  updateFilters: () => {},
  setShowFilters: () => {}
};

// Export helper functions for unit testing
export { checkIsOyM, getSetValue, getSetFieldName };
